import { Stack, Typography, Box, Divider, Tooltip, Link, Button, Alert } from '@mui/material';
import InfoIcon from '@mui/icons-material/Info';
import { OpportunityFormData } from '@/features/opportunities/form/types.ts';
import EurCurrencyField from '@/components/form/EurCurrencyField.tsx';
import { formatCurrency, formatPercentage } from '@/utils/misc.ts';
import TextFieldWithCheck from '@/components/form/TextFieldWithCheck.tsx';
import { useMemo, useState, FC } from 'react';
import { calculateTransactionResults } from './utils';
import { useTransactionCosts } from '@/lib/api/financial-calculations.ts';
import { OpportunityDTO } from '@inversor-hub/types';

interface Props {
  formData: OpportunityFormData;
  handleChangeField: (field: string, value: string | number | null) => void;
  handleChangeFields: (changeset: Record<string, number | string>) => void;
  onNavigateToMonthlyRent?: () => void;
  opportunity?: OpportunityDTO;
}

interface MonthlyRentLinkProps {
  onClick: () => void;
}

const MonthlyRentLink: FC<MonthlyRentLinkProps> = ({ onClick }) => {
  return (
    <Typography variant="body2" color="text.secondary" sx={{ fontSize: 11, mt: 0.5 }}>
      Calculado con los valores de la sección{' '}
      <Link
        component="button"
        type="button"
        onClick={onClick}
        sx={{
          textDecoration: 'underline',
          cursor: 'pointer',
          color: 'primary.main',
          fontWeight: 500,
        }}
      >
        Flujo mensual
      </Link>
    </Typography>
  );
};

const OpportunityTransactionDetailsSection = ({
  formData,
  handleChangeField,
  handleChangeFields,
  onNavigateToMonthlyRent,
  opportunity,
}: Props) => {
  const [isCalculating, setIsCalculating] = useState(false);

  const { data: transactionCosts, isLoading: isLoadingCosts } = useTransactionCosts(
    (formData.deedAmount ?? 0) || (formData.negotiatedPrice ?? 0),
    formData.province
  );

  const handleCalculateAutomatically = async () => {
    if (!transactionCosts) return;

    setIsCalculating(true);

    try {
      // Add a small delay for better UX feedback
      await new Promise((resolve) => setTimeout(resolve, 300));

      handleChangeFields({
        purchaseTaxItp: transactionCosts.itp.calculatedItp,
      });
    } finally {
      setIsCalculating(false);
    }
  };

  const canCalculate = Boolean(
    transactionCosts &&
      ((formData.deedAmount ?? 0) > 0 || (formData.negotiatedPrice ?? 0) > 0) &&
      formData.province
  );

  const hasAutoCalculatedItp = Boolean(
    transactionCosts && formData.purchaseTaxItp === transactionCosts.itp.calculatedItp
  );

  const calculationResults = useMemo(() => calculateTransactionResults(formData), [formData]);

  const {
    grossMonthlyIncomeTraditional,
    grossReturnTraditional,
    netMonthlyIncomeTraditional,
    netMonthlyIncomeRooms,
    netReturnRooms,
    netReturnTraditional,
    grossReturnRooms,
    grossMonthlyIncomeRooms,
    grossReturnFlipping,
    netReturnFlipping,
    totalExpenses,
  } = calculationResults;

  const handleMonthlyRentClick = () => {
    if (onNavigateToMonthlyRent) {
      onNavigateToMonthlyRent();
    }
  };

  const renderFinancialAnalysisByType = () => {
    switch (formData.type) {
      case 'TRADITIONAL_RENT':
        return (
          <>
            <Box>
              <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 500 }}>
                Ingresos Mensuales Brutos
              </Typography>
              <EurCurrencyField
                id="grossMonthlyIncomeTraditional"
                name="grossMonthlyIncomeTraditional"
                value={grossMonthlyIncomeTraditional}
                disabled
              />
              <MonthlyRentLink onClick={handleMonthlyRentClick} />
            </Box>

            <Box>
              <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 500 }}>
                Ingresos Mensuales Netos
              </Typography>
              <EurCurrencyField
                id="netMonthlyIncomeTraditional"
                name="netMonthlyIncomeTraditional"
                disabled
                value={netMonthlyIncomeTraditional}
              />
              <MonthlyRentLink onClick={handleMonthlyRentClick} />
            </Box>

            <Box sx={{ mb: 0 }}>
              <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 500 }}>
                Rentabilidad Bruta
              </Typography>
              <TextFieldWithCheck
                id="grossReturnTraditional"
                name="grossReturnTraditional"
                variant="outlined"
                disabled
                value={formatPercentage(grossReturnTraditional)}
              />
              <Typography variant="body1" sx={{ fontSize: 11, mt: 1 }}>
                [INGRESO TOTAL ANUAL ESPERADO / COSTE DEL INMUEBLE (INCLUYENDO GASTOS E ITP)] x 100
              </Typography>
            </Box>

            <Box sx={{ mb: 0 }}>
              <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 500 }}>
                Rentabilidad Neta
              </Typography>
              <TextFieldWithCheck
                id="netReturnTraditional"
                name="netReturnTraditional"
                variant="outlined"
                disabled
                value={formatPercentage(netReturnTraditional)}
              />
              <Typography variant="body1" sx={{ fontSize: 11, mt: 1 }}>
                [(INGRESO TOTAL ANUAL ESPERADO - GASTOS) / COSTE INMUEBLE (INCLUYENDO GASTOS E ITP)]
                x 100
              </Typography>
            </Box>
          </>
        );

      case 'ROOM_RENT':
        return (
          <>
            <Box>
              <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 500 }}>
                Ingresos Mensuales Brutos
              </Typography>
              <EurCurrencyField
                id="grossMonthlyIncomeRooms"
                name="grossMonthlyIncomeRooms"
                value={grossMonthlyIncomeRooms}
                disabled
              />
              <MonthlyRentLink onClick={handleMonthlyRentClick} />
            </Box>

            <Box>
              <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 500 }}>
                Ingresos Mensuales Netos
              </Typography>
              <EurCurrencyField
                id="netMonthlyIncomeRooms"
                name="netMonthlyIncomeRooms"
                disabled
                value={netMonthlyIncomeRooms}
              />
              <MonthlyRentLink onClick={handleMonthlyRentClick} />
            </Box>

            <Box sx={{ mb: 0 }}>
              <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 500 }}>
                Rentabilidad Bruta
              </Typography>
              <TextFieldWithCheck
                id="grossReturnRooms"
                name="grossReturnRooms"
                variant="outlined"
                disabled
                value={formatPercentage(grossReturnRooms)}
              />
              <Typography variant="body1" sx={{ fontSize: 11, mt: 1 }}>
                [INGRESO TOTAL ANUAL ESPERADO / COSTE DEL INMUEBLE (INCLUYENDO GASTOS E ITP)] x 100
              </Typography>
            </Box>

            <Box sx={{ mb: 0 }}>
              <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 500 }}>
                Rentabilidad Neta
              </Typography>
              <TextFieldWithCheck
                id="netReturnRooms"
                name="netReturnRooms"
                variant="outlined"
                disabled
                value={formatPercentage(netReturnRooms)}
              />
              <Typography variant="body1" sx={{ fontSize: 11, mt: 1 }}>
                [(INGRESO TOTAL ANUAL ESPERADO - GASTOS) / COSTE INMUEBLE (INCLUYENDO GASTOS E ITP)]
                x 100
              </Typography>
            </Box>
          </>
        );

      case 'VACATIONAL':
      case 'CHANGE_OF_USE':
        return (
          <>
            <Box>
              <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 500 }}>
                Ingresos Mensuales Brutos
              </Typography>
              <EurCurrencyField
                id="grossMonthlyIncomeTraditional"
                name="grossMonthlyIncomeTraditional"
                value={grossMonthlyIncomeTraditional}
                disabled
              />
              <MonthlyRentLink onClick={handleMonthlyRentClick} />
            </Box>

            <Box>
              <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 500 }}>
                Ingresos Mensuales Netos
              </Typography>
              <EurCurrencyField
                id="netMonthlyIncomeTraditional"
                name="netMonthlyIncomeTraditional"
                disabled
                value={netMonthlyIncomeTraditional}
              />
              <MonthlyRentLink onClick={handleMonthlyRentClick} />
            </Box>

            <Box sx={{ mb: 0 }}>
              <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 500 }}>
                Rentabilidad Bruta
              </Typography>
              <TextFieldWithCheck
                id="grossReturnTraditional"
                name="grossReturnTraditional"
                variant="outlined"
                disabled
                value={formatPercentage(grossReturnTraditional)}
              />
              <Typography variant="body1" sx={{ fontSize: 11, mt: 1 }}>
                [INGRESO TOTAL ANUAL ESPERADO / COSTE DEL INMUEBLE (INCLUYENDO GASTOS E ITP)] x 100
              </Typography>
            </Box>

            <Box sx={{ mb: 0 }}>
              <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 500 }}>
                Rentabilidad Neta
              </Typography>
              <TextFieldWithCheck
                id="netReturnTraditional"
                name="netReturnTraditional"
                variant="outlined"
                disabled
                value={formatPercentage(netReturnTraditional)}
              />
              <Typography variant="body1" sx={{ fontSize: 11, mt: 1 }}>
                [(INGRESO TOTAL ANUAL ESPERADO - GASTOS) / COSTE INMUEBLE (INCLUYENDO GASTOS E ITP)]
                x 100
              </Typography>
            </Box>
          </>
        );

      case 'FLIPPING':
        return (
          <>
            <Box>
              <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 500 }}>
                Precio de Venta Estimado
              </Typography>
              <EurCurrencyField
                id="estimatedSalePrice"
                name="estimatedSalePrice"
                label="Precio de venta estimado (*)"
                value={formData.estimatedSalePrice ?? null}
                onChange={(value) => handleChangeField('estimatedSalePrice', value)}
              />
              <Typography variant="body2" sx={{ fontSize: 11, mt: 1, color: 'text.secondary' }}>
                Precio estimado de venta del inmueble una vez renovado
              </Typography>
            </Box>

            <Box sx={{ mb: 0 }}>
              <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 500 }}>
                Ganancia Estimada
              </Typography>
              <EurCurrencyField
                id="netProfitFlipping"
                name="netProfitFlipping"
                value={(formData.estimatedSalePrice ?? 0) - totalExpenses}
                disabled
              />
              <Typography variant="body1" sx={{ fontSize: 11, mt: 1 }}>
                [PRECIO DE VENTA - TODOS LOS COSTES (incluye impuestos, notaría, registro, etc.)]
              </Typography>
            </Box>

            <Box sx={{ mb: 0 }}>
              <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 500 }}>
                Rentabilidad Bruta
              </Typography>
              <TextFieldWithCheck
                id="grossReturnFlipping"
                name="grossReturnFlipping"
                variant="outlined"
                disabled
                value={formatPercentage(grossReturnFlipping)}
              />
              <Typography variant="body1" sx={{ fontSize: 11, mt: 1 }}>
                [(PRECIO VENTA - PRECIO COMPRA - REFORMA) / (PRECIO COMPRA + REFORMA)] x 100
              </Typography>
            </Box>

            <Box sx={{ mb: 0 }}>
              <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 500 }}>
                Rentabilidad Neta
              </Typography>
              <TextFieldWithCheck
                id="netReturnFlipping"
                name="netReturnFlipping"
                variant="outlined"
                disabled
                value={formatPercentage(netReturnFlipping)}
              />
              <Typography variant="body1" sx={{ fontSize: 11, mt: 1 }}>
                [(PRECIO VENTA - TODOS LOS COSTES) / TODOS LOS COSTES] x 100
              </Typography>
            </Box>
          </>
        );

      default:
        return null;
    }
  };

  return (
    <Stack spacing={3}>
      <Box>
        <Typography sx={{ mb: 2, fontWeight: 600, color: 'primary.main' }}>Compraventa</Typography>
        <Stack spacing={2}>
          <EurCurrencyField
            id="nonNegotiatedPrice"
            name="nonNegotiatedPrice"
            value={formData.nonNegotiatedPrice}
            onChange={(value) => handleChangeField('nonNegotiatedPrice', value)}
            label="Precio sin negociar"
            allowDecimalScale={false}
          />
          <EurCurrencyField
            id="negotiatedPrice"
            name="negotiatedPrice"
            label="Precio negociado (precio compra)"
            value={formData.negotiatedPrice}
            onChange={(value) => handleChangeField('negotiatedPrice', value)}
            allowDecimalScale={false}
          />

          {/* Price Change History Information */}
          {((opportunity?.previousPrice && opportunity?.priceChangedAt) ||
            opportunity?.status === 'PENDING') && (
            <Alert severity="info" icon={<InfoIcon fontSize="inherit" />} sx={{ borderRadius: 1 }}>
              {opportunity?.status === 'PENDING' && (
                <Typography variant="body2" fontWeight={500} mb={2}>
                  Los inversores verán el cambio en el precio negociado.
                </Typography>
              )}

              <Typography variant="body2" fontWeight={500}>
                Último cambio de precio:
              </Typography>
              {opportunity?.previousPrice && opportunity?.priceChangedAt ? (
                <Typography variant="body2" sx={{ mt: 0.5 }}>
                  Precio anterior: {formatCurrency(opportunity.previousPrice)} • Cambiado el{' '}
                  {new Date(opportunity.priceChangedAt).toLocaleDateString('es-ES', {
                    year: 'numeric',
                    month: 'long',
                    day: 'numeric',
                  })}
                </Typography>
              ) : (
                <Typography variant="body2" sx={{ mt: 0.5 }}>
                  Aún no se ha registrado ningún cambio de precio.
                </Typography>
              )}
            </Alert>
          )}

          <EurCurrencyField
            id="deedAmount"
            name="deedAmount"
            label="¿Cantidad a escriturar?"
            value={formData.deedAmount}
            onChange={(value) => handleChangeField('deedAmount', value)}
            allowDecimalScale={false}
          />
          <div style={{ marginBottom: 0 }}>
            <EurCurrencyField
              id="referenceValue"
              name="referenceValue"
              label="¿Valor de referencia?"
              value={formData.referenceValue}
              onChange={(value) => handleChangeField('referenceValue', value)}
              allowDecimalScale={false}
            />
            <Typography variant="body1" sx={{ fontSize: 11 }}>
              (Se puede consultar online. Hace falta certificado digital o similar -
              https://www1.sedecatastro.gob.es/Accesos/SECAccvr.aspx ...)
            </Typography>
          </div>
        </Stack>
      </Box>

      <Divider />

      <Box>
        <Typography sx={{ mb: 2, fontWeight: 600, color: 'primary.main' }}>
          Gastos de Compraventa
        </Typography>
        <Stack spacing={2}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Box sx={{ flex: 1 }}>
              <EurCurrencyField
                key={`itp-${transactionCosts?.itp.calculatedItp || 'manual'}`}
                id="purchaseTaxItp"
                name="purchaseTaxItp"
                label={`Impuesto ITP${
                  hasAutoCalculatedItp && formData.purchaseTaxItp !== undefined
                    ? ' (calculado automáticamente)'
                    : ''
                }`}
                value={formData.purchaseTaxItp}
                onChange={(value) => handleChangeField('purchaseTaxItp', value)}
                allowDecimalScale={false}
                disabled={isLoadingCosts}
              />
            </Box>
            <Tooltip
              title={
                transactionCosts
                  ? `ITP ${transactionCosts.itp.community}: ${
                      transactionCosts.itp.rate
                    }% aplicado sobre ${formatCurrency(
                      transactionCosts.itp.propertyValue
                    )} = ${formatCurrency(transactionCosts.itp.calculatedItp)}`
                  : 'Cálculo automático basado en la comunidad autónoma y valor de escritura'
              }
            >
              <span>
                <Button
                  variant="outlined"
                  onClick={handleCalculateAutomatically}
                  disabled={!canCalculate || isCalculating || isLoadingCosts}
                >
                  {isCalculating ? 'Calculando...' : isLoadingCosts ? 'Cargando...' : 'Calcular'}
                </Button>
              </span>
            </Tooltip>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Box sx={{ flex: 1 }}>
              <EurCurrencyField
                id="notaryExpenses"
                name="notaryExpenses"
                label="Notaría"
                value={formData.notaryExpenses}
                onChange={(value) => handleChangeField('notaryExpenses', value)}
                allowDecimalScale={false}
              />
            </Box>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Box sx={{ flex: 1 }}>
              <EurCurrencyField
                id="registryExpenses"
                name="registryExpenses"
                label="Registro"
                value={formData.registryExpenses}
                onChange={(value) => handleChangeField('registryExpenses', value)}
                allowDecimalScale={false}
              />
            </Box>
          </Box>
          <EurCurrencyField
            id="renovationCost"
            name="renovationCost"
            label="Reforma estimada"
            value={formData.renovationCost}
            onChange={(value) => handleChangeField('renovationCost', value)}
            allowDecimalScale={false}
          />
          <EurCurrencyField
            id="realtorCommission"
            name="realtorCommission"
            label="Comisión inmobiliaria"
            value={formData.realtorCommission}
            onChange={(value) => handleChangeField('realtorCommission', value)}
            allowDecimalScale={false}
          />
          <EurCurrencyField
            id="psiCommission"
            name="psiCommission"
            label="Comisión PSI"
            value={formData.psiCommission}
            onChange={(value) => handleChangeField('psiCommission', value)}
            allowDecimalScale={false}
          />
          <EurCurrencyField
            id="purchaseExpenses"
            name="purchaseExpenses"
            label="Otros gastos"
            value={formData.purchaseExpenses}
            onChange={(value) => handleChangeField('purchaseExpenses', value)}
            allowDecimalScale={false}
          />
        </Stack>
      </Box>

      <Divider />

      <Box>
        <Typography sx={{ mb: 2, fontWeight: 600, color: 'primary.main' }}>
          Análisis Financiero
        </Typography>
        <Stack spacing={2}>
          <EurCurrencyField
            id="totalExpenses"
            name="totalExpenses"
            label="Gastos operación compra"
            value={totalExpenses}
            disabled
            allowDecimalScale={false}
          />

          {renderFinancialAnalysisByType()}
        </Stack>
      </Box>
    </Stack>
  );
};

export default OpportunityTransactionDetailsSection;
